import { useState } from 'react'
import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { StatusBucket } from '@/lib/docusign-data'
import { PanelEmpty, PanelError, PanelLoading } from './PanelState'

/** In flight is blue, finished is green, dead is red, unclassified is grey. */
const toneClass: Record<StatusBucket['tone'], string> = {
  open: 'bg-info',
  done: 'bg-primary-bright',
  dead: 'bg-critical',
  neutral: 'bg-fg-subtle',
}

interface EnvelopeStatusPanelProps {
  buckets: StatusBucket[]
  /** Envelopes behind the panel; the buckets sum to this. */
  total?: number | null
  /** The payload's own caveats about how the figures were counted. */
  notes?: string[]
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
}

/**
 * Envelopes by current status, for the selected period.
 *
 * This used to be a "Document Pipeline" funnel, drawn widest-first with every
 * bar measured against Sent. Live data showed that to be wrong: the statuses are
 * mutually exclusive buckets that sum to the total, so completed (1,764) sits
 * above sent (634) — an envelope that completed has stopped being counted as
 * sent. As a funnel that read as a 278% completion rate. As a breakdown it reads
 * as what it is: most of the account's envelopes are done, 634 are still out.
 *
 * Bars are scaled against the largest bucket, and the total is printed so the
 * parts can be checked against the whole.
 */
export function EnvelopeStatusPanel({
  buckets,
  total,
  notes,
  isLoading,
  error,
  onRetry,
}: EnvelopeStatusPanelProps) {
  /** Status row under the cursor. */
  const [hovered, setHovered] = useState<string | null>(null)

  const top = Math.max(...buckets.map((b) => b.count), 1)
  const sum = buckets.reduce((n, b) => n + b.count, 0)

  return (
    <section className="border-line bg-surface flex flex-col rounded-lg border p-5">
      <div className="border-line flex items-center justify-between gap-3 border-b pb-3">
        <h2 className="text-fg-muted flex items-center gap-1.5 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          Envelope Status
          {notes?.length ? (
            // The notes matter here more than anywhere: they are what says these
            // counts span the whole shared DocuSign account, staging included.
            <span title={notes.join('\n\n')} className="cursor-help">
              <Info
                aria-label="How these figures are counted"
                className="text-fg-subtle size-3.5"
              />
            </span>
          ) : null}
        </h2>
        {typeof total === 'number' && (
          <span className="text-fg-subtle font-mono text-[11px] tabular-nums">
            {total.toLocaleString()} total
          </span>
        )}
      </div>

      {isLoading ? (
        <PanelLoading className="flex-1" />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} className="flex-1" />
      ) : buckets.length === 0 ? (
        <PanelEmpty label="No envelopes in this period." className="flex-1" />
      ) : (
        <dl className="mt-4 flex flex-1 flex-col justify-center gap-3">
          {buckets.map((bucket) => {
            const active = hovered === bucket.label
            return (
              <div
                key={bucket.label}
                onMouseEnter={() => setHovered(bucket.label)}
                onMouseLeave={() => setHovered(null)}
                className={cn(
                  '-mx-2 flex cursor-default items-center gap-3 rounded-md px-2 py-1 transition-colors',
                  active && 'bg-surface-2',
                )}
              >
                <dt
                  className={cn(
                    'w-20 shrink-0 text-right font-mono text-[13px] transition-colors',
                    active ? 'text-fg' : 'text-fg-muted',
                  )}
                >
                  {bucket.label}
                </dt>
                <div className="bg-surface-3 h-2.5 min-w-0 flex-1 rounded-full">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      toneClass[bucket.tone],
                      active && 'brightness-125',
                    )}
                    style={{ width: `${(bucket.count / top) * 100}%` }}
                  />
                </div>
                {/* The share replaces the count on hover rather than sitting
                    beside it — six permanent percentages would crowd a panel
                    whose job is the shape of the bars. Same width either way, so
                    nothing shifts. */}
                <dd
                  className={cn(
                    'w-14 shrink-0 text-right font-mono text-[13px] tabular-nums',
                    active ? 'text-fg-subtle' : 'text-fg',
                  )}
                >
                  {active && sum > 0
                    ? `${Math.round((bucket.count / sum) * 100)}%`
                    : bucket.count.toLocaleString()}
                </dd>
              </div>
            )
          })}
        </dl>
      )}
    </section>
  )
}
